import type {
  CollectionsTransportWorker,
  UnproxiedEditorSDK,
} from '@wix/wix-data-client-common-standalone'
import {
  createError,
  findError,
  isCollectionNotFoundError,
  isWixCodeDisabledModeError,
} from '@wix/wix-data-client-common-standalone'
import type { Schema } from '@wix/wix-data-schema-types'
import type { AppApi } from '@wix/wix-data-client-common'
import type { SchemasClientAdapter } from './types'
import type { Action } from '../schemaContextMenuActions'

export const createCollectionsTransportWorker = ({
  editorSDK,
  adapter,
  appToken,
  finalAppApiPromise,
  getActionsBySchema,
  getEnterpriseBrandName,
}: {
  appToken: string
  editorSDK: UnproxiedEditorSDK
  adapter: SchemasClientAdapter
  finalAppApiPromise: Promise<AppApi>
  getEnterpriseBrandName: (parameters: {
    schemas: Schema[]
  }) => Promise<string | undefined>
  getActionsBySchema: (parameters: {
    schemas: Schema[]
  }) => Promise<Record<string, Action[]>>
}): CollectionsTransportWorker => {
  const throwError = async (
    message: string,
    cause: unknown,
    extras: any = {},
  ) => {
    if (findError(cause, isWixCodeDisabledModeError)) {
      return
    }
    const metaSiteId = await editorSDK.info
      .getMetaSiteId(appToken)
      .catch(() => {})
    throw createError('CollectionsTransportWorker failure during ' + message, {
      cause,
      data: {
        metaSiteId,
        ...extras,
      },
    })
  }

  const updateAppStore = async () => {
    const schemas = await adapter.listSchemas({
      includeDeletedCollections: false,
    })
    const [actionsBySchema, enterpriseBrandName] = await Promise.all([
      getActionsBySchema({ schemas }),
      getEnterpriseBrandName({ schemas }),
    ])

    const appApi = await finalAppApiPromise
    await appApi.updateAppState({
      schemas,
      actionsBySchema,
      enterpriseBrandName,
    })
  }

  const updateAppStoreOrReportError = async () => {
    try {
      await updateAppStore()
    } catch {}
  }

  const clearCache = async () => await adapter.clearCache()

  const refreshCache = async () => {
    await adapter.clearCache()
    updateAppStoreOrReportError()
  }

  const listSchemas = async ({
    includeDeletedCollections = false,
    includeHiddenAppCollections = false,
  } = {}) => {
    try {
      return await adapter.listSchemas({
        includeDeletedCollections,
        includeHiddenAppCollections,
      })
    } catch (err) {
      await throwError('listSchemas', err, {
        includeDeletedCollections,
        includeHiddenAppCollections,
      })
      return
    }
  }

  const loadSchema = async (collectionId: string) => {
    try {
      return await adapter.loadSchema(collectionId)
    } catch (err) {
      if (findError(err, isCollectionNotFoundError)) {
        throw err
      }
      await throwError('loadSchema', err, { collectionId })
      return
    }
  }

  const refreshIfConcurrentEditingError = async (error: unknown) => {
    const appApi = await finalAppApiPromise
    const errorIsConcurrentEditingError =
      await appApi.checkIfErrorIsConcurrentEditingError(error)

    if (errorIsConcurrentEditingError) {
      await appApi.refreshSchemasCache()
    }
  }

  const saveSchema = async (collectionId: string, schema: Schema) => {
    try {
      await adapter.saveSchema(collectionId, schema)
    } catch (error) {
      refreshIfConcurrentEditingError(error)
      await throwError('saveSchema', error, { collectionId })
    }
    updateAppStoreOrReportError()
  }

  const removeSchema = async (collectionId: string) => {
    try {
      await adapter.removeSchema(collectionId)
    } catch (err) {
      await throwError('removeSchema', err, { collectionId })
    }
    updateAppStoreOrReportError()
  }

  const restoreSchema = async (collectionId: string) => {
    try {
      await adapter.restoreSchema(collectionId)
    } catch (err) {
      await throwError('restoreSchema', err, { collectionId })
    }
    updateAppStoreOrReportError()
  }

  updateAppStoreOrReportError()

  return {
    private: {
      refreshCache,
      clearCache,
    },
    public: {
      listSchemas,
      loadSchema,
      saveSchema,
      removeSchema,
      restoreSchema,
    },
  }
}
